<script setup lang="ts">
import { computed, inject, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useUxData } from "@/stores/ux-data";
import { useAppDataStore } from "@/stores/app-data";
import { useCharacterDataStore } from "@/stores/character-data";
import { useGuidedSetupStore } from "@/stores/guided-setup-store";
import { usePredicateStore } from "@/stores/predicate-store";
import { useAuthDataStore } from "@/stores/auth-data";
import { useWorldBrowser } from "@/composables/useWorldBrowser";
import { useCharacterArchetype } from "@/composables/useCharacterArchetype";
import {handleError, stripHtml} from "@/appdata/globals";
import WorldBrowser from "@/components/atoms/WorldBrowser.vue";
import type { SwadeCharacterInterface } from "@/types/characters";
import type { WorldInterface } from "@/types/worlds";
import type { ColumnConfig } from "@/types/browser";
import { FilterOperator, type FilterDescriptor } from "@progress/kendo-data-query";
import type { IntlService } from "@progress/kendo-vue-intl";
import type {SettingElementInterface, SwadeEdgeInterface} from "@/types/items.ts";

const props = defineProps<{
    stepIndex: number;
}>();

const character = defineModel<SwadeCharacterInterface | null>('character');

const ux = useUxData();
const store = useAppDataStore();
const characterData = useCharacterDataStore();
const setupStore = useGuidedSetupStore();
const predicateStore = usePredicateStore();
const auth = useAuthDataStore();
const kendo = inject('kendo') as IntlService;

const { worldFilters, worldPredicates } = storeToRefs(predicateStore);

const { archetype, archetypeLabel } = useCharacterArchetype(character);

const worldBrowserRef = ref<InstanceType<typeof WorldBrowser> | null>(null);
const hasAvailableWorlds = ref(false);

const selectedWorld = computed(() => character.value?.world);

// This step owns the character name as well as world selection, so it always renders —
// but the world half only appears when there is something to act on: an assignable world
// (owned, or invited to as a player — see below), or the ability to create one.
const canChooseWorld = computed(() => auth.canCreateWorld || hasAvailableWorlds.value);

const isValid = computed(() => (character.value?.name ?? '').length > 0);

watch(isValid, (valid) => {
    setupStore.validateStep(props.stepIndex, valid);
}, { immediate: true });

const columns: ColumnConfig[] = [
    {
      field: 'name',
      columnClass: ['name-column','fw-semibold'],
      header: ux.labels?.guidedSetup?.character?.worldName ?? 'Name'
    },
];

// Single shared sequence for "this draft now targets <world | no world>". Replaces the
// duplication that previously lived in onMounted (null variant) and onWorldSelected.
// Async since applySettingRulesToCharacter became awaitable: updateBuildValidation must run
// against a character that has actually received the world's core skills, edges and hindrances.
const applyWorldToDraft = async (world: WorldInterface | null) => {
    const system = world?.system ?? store.system;
    const existingName = character.value?.name;
    const existingSummary = character.value?.summary ?? null;
    const existingArchetype = character.value?.additionalData?.archetype;

    const newChar = characterData.newCharacter({
        name: world
            ? (existingName || kendo.format('{0} Character', world?.name ?? 'New'))
            : (existingName ?? ''),
        system,
    } as SwadeCharacterInterface);

    // newCharacter() falls back to a lingering store.world when none is passed; force ours.
    newChar.world = world ?? null;

    // The archetype and background sit above the campaign picker, so they are usually typed before a
    // world is chosen: they come along with the name.
    newChar.summary = existingSummary;
    if (existingArchetype) {
        newChar.additionalData = { ...newChar.additionalData, archetype: existingArchetype };
    }

    character.value = newChar;
    setupStore.setCharacterDraft(newChar);

    const systemDefaults = system?.defaults;
    if (systemDefaults) {
        const mergedDefaults = characterData.combineSettingRules(systemDefaults, world);
        await characterData.applySettingRulesToCharacter(newChar, mergedDefaults);
        characterData.updateBuildValidation(newChar, mergedDefaults);
    }
};

// A character is always assigned to somebody's world: one the user owns, or one they
// accepted a player invitation to. The unowned library (owner === null) is reference
// content and can't hold characters, so it is excluded here for every user.
const hasOwner = (world: WorldInterface) => !!world?.owner;

// The owner.uuid EqualTo scope is STRIPPED rather than taken from the global ownedOnly slot, and
// this step's own owner predicate is appended instead, so that what the step asks for does not
// depend on what that slot holds: it is the world list's and a world's element tab's to set.
// (Every mounted WorldFinder used to write it as well — until 2026-09-23 on each re-render, which
// this browser's own fetch caused. A finder keeps its own scope now and writes nothing shared.)
// The API reads any owner criterion on a world listing as "my worlds" — the user's own and the
// ones they play in — whatever its operator and value (WorldRepository::index in dicey-api);
// with no owner criterion it lists the unowned library as well. The IsNotNull predicate is
// therefore what keeps the library out: a character is always assigned to somebody's world.
const assignableWorldPredicates = computed<FilterDescriptor[]>(() => [
    ...worldPredicates.value.filter((filter: FilterDescriptor) =>
        !(filter.field === 'owner.uuid' && filter.operator === FilterOperator.EqualTo)),
    { field: 'owner.uuid', operator: FilterOperator.IsNotNull, value: true },
]);

const worldFetchFn = async (url: string | null, predicates: FilterDescriptor[]) => {
    const result = await store.loadWorldOptions(store.system, url, predicates, false);
    // A request that fails answers with nothing, and the browser keeps the list it had. The
    // store's world list is only this request's answer when the request has one: otherwise it
    // holds what some other world surface last asked for.
    if (!result) return undefined;
    return {
        // Re-check client-side: paging links carry the original query, and a library world
        // slipping through would be selectable and then rejected on save.
        results: (store.worlds ?? []).filter(hasOwner),
        metadata: result.metadata ?? { total: null, start: null, end: null },
        links: result.links ?? { next: null, current: null, previous: null },
    };
};

const onWorldConfirmed = async (world: WorldInterface) => {
    try {
        const full = await store.retrieveWorld(world);
        if (!full) return;
        await applyWorldToDraft(full);
    } catch (error) {
        handleError(error);
    }
};

const browser = useWorldBrowser({
    predicates: assignableWorldPredicates as any,
    filterRefs: worldFilters as any,
    fetchFn: worldFetchFn,
    onConfirm: onWorldConfirmed,
});

const configurePredicates = () => {
    predicateStore.system = store.system;
    // No ownedOnly write: assignableWorldPredicates carries the step's own owner predicate, and
    // the global flag is not this step's to set.
};

onMounted(async () => {
    if (!character.value) {
        await applyWorldToDraft(null);
    }

    configurePredicates();
    // This asks whether the user has ANY assignable world, so it must not carry a search. The
    // keyword slot is shared with the world list and the other world surfaces, and text left in
    // it would turn "none match that search" into "no worlds", hiding world selection.
    worldFilters.value.stringPartial = '';
    await browser.fetch();
    hasAvailableWorlds.value = browser.dataItems.value.length > 0;
});

const openBrowser = () => {
    configurePredicates();
    browser.open();
    worldBrowserRef.value?.show();
};

const onConfirmAndClose = (world: WorldInterface) => {
    browser.onConfirm(world);
    worldBrowserRef.value?.hide();
};

const onWorldCleared = async () => {
    await applyWorldToDraft(null);
};

const onCreateCampaign = () => {
    setupStore.startWorldFlow(store.system);
};
</script>

<template>
    <div class="character-world-step">
      <template v-if="character">
        <div class="form-group mb-3">
          <label for="new-character-name" class="form-label fw-semibold">{{ ux.labels?.tools?.modalCharacterNew?.name?.label }}</label>
          <input id="new-character-name" type="text" class="form-control form-control-lg"
                 :placeholder="ux.labels?.tools?.modalCharacterNew?.name?.placeholder"
                 v-model="character.name"/>
        </div>

        <div class="form-group mb-3">
          <label for="new-character-archetype" class="form-label fw-semibold">{{ archetypeLabel }}</label>
          <input id="new-character-archetype" type="text" maxlength="100" class="form-control" v-model="archetype"/>
        </div>

        <div class="form-group mb-3">
          <label for="new-character-background" class="form-label fw-semibold">{{ ux.labels?.swade?.data?.characters?.summary ?? 'Background/Summary' }}</label>
          <textarea id="new-character-background" rows="4" class="form-control" v-model="character.summary"></textarea>
        </div>
      </template>

      <div v-if="canChooseWorld" class="campaign-selection form-group mb-3">
        <div class="d-flex flex-column-reverse flex-md-row gap-3">
          <div class="world-browser-trigger">
            <button
              type="button"
              class="btn btn-outline-secondary open-world-browser text-nowrap w-md-auto w-100"
              @click="openBrowser"
            >
              <span class="fa-light fa-book-open-alt"></span>
              {{ ux.labels?.guidedSetup?.character?.worldLabel ?? 'Choose a Campaign Setting' }}
            </button>
          </div>
          <div class="step-help flex-grow-1">
            <label class="form-label fw-semibold" v-html="ux.labels?.guidedSetup?.character?.worldLabel"></label>
            <p class="form-text mt-0 mb-0" v-html="ux.labels?.guidedSetup?.character?.worldHelp"></p>
          </div>
        </div>

        <div v-if="selectedWorld" class="selected-world mt-3 p-3 border rounded bg-light">
            <div class="d-flex justify-content-between align-items-start">
              <h6 class="mb-1">{{ selectedWorld.name }}</h6>
              <button type="button" class="btn btn-sm btn-link text-muted p-0"
                      @click="onWorldCleared"
              >
                <span class="fa-light fa-times"></span>
              </button>
            </div>
            <p v-if="selectedWorld.summary" class="mb-0 small text-muted" v-html="selectedWorld.summary"></p>
        </div>
      </div>

      <WorldBrowser
        v-if="canChooseWorld"
        ref="worldBrowserRef"
        :title="ux.labels?.guidedSetup?.character?.worldBrowseTitle ?? 'Campaign Settings'"
        :data-items="browser.dataItems.value"
        placement="bottom"
        :columns="columns"
        :selected-element="browser.selectedElement.value"
        :loading="browser.loading.value"
        :metadata="browser.metadata.value"
        :links="browser.links.value"
        :can-create="auth.canCreateWorld"
        @select="browser.onSelect"
        @confirm="onConfirmAndClose"
        @search="browser.onSearch"
        @next="browser.onNextPage"
        @previous="browser.onPreviousPage"
        @close="browser.close"
        @create-campaign="onCreateCampaign"
      />
    </div>
</template>
